import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import { clarityEvent } from '../../utils/clarity';
import { trackEvent } from '../../utils/googleAnalytics';
import './Faq.css';

// Static in-app FAQ. Bodies are plain JSX (no dangerouslySetInnerHTML), so
// there is nothing to sanitize and no extra dependency to pull in.
const FAQ_ITEMS = [
    {
        id: 'api-keys',
        title: 'How do I connect my Binance API keys?',
        body: (
            <>
                <p>
                    Go to <Link to="/settings">Settings</Link> and paste your
                    API Key and Secret Key. We recommend creating a dedicated
                    key for the bot with <strong>Read</strong> and{' '}
                    <strong>Spot &amp; Margin Trading</strong> permissions only.
                </p>
                <p className="mb-0">
                    Never enable <strong>Withdrawals</strong> on a bot key, and
                    restrict the key to your server IP if you can. After saving,
                    the app validates the key against Binance and stores it
                    encrypted.
                </p>
            </>
        ),
    },
    {
        id: 'key-safety',
        title: 'Are my API keys safe?',
        body: (
            <>
                <p className="mb-0">
                    Keys are encrypted at rest and never returned to the browser
                    in plain text. The bot can place and cancel orders, but it
                    cannot withdraw funds. You can revoke access at any time
                    from the Binance dashboard.
                </p>
            </>
        ),
    },
    {
        id: 'alerts',
        title: 'How do I get notified about what the bot does?',
        body: (
            <>
                <p className="mb-0">
                    In <Link to="/settings">Settings</Link> you can connect
                    Telegram or your own SMTP server. You will then receive
                    messages for order fills, errors and automation events. If
                    no channel is configured, the bot still trades but you will
                    not be notified.
                </p>
            </>
        ),
    },
    {
        id: 'automations',
        title: 'What is an automation?',
        body: (
            <>
                <p className="mb-0">
                    An automation links a <em>strategy</em> (when to act) with
                    an <em>order template</em> (what to send to the exchange).
                    Once active, the bot evaluates the strategy on live data and
                    places the order when the conditions match. You can start
                    and stop each automation individually from{' '}
                    <Link to="/automations">Automations</Link>.
                </p>
            </>
        ),
    },
    {
        id: 'backtests',
        title: 'How do backtests work?',
        body: (
            <>
                <p className="mb-0">
                    A <Link to="/automations/backtests">backtest</Link> replays
                    an automation over historical candles so you can see how it
                    would have performed before risking real funds. Backtests
                    ignore whether the automation is currently active and are
                    limited to the periods the platform allows.
                </p>
            </>
        ),
    },
    {
        id: 'monitors',
        title: 'What are monitors for?',
        body: (
            <>
                <p className="mb-0">
                    <Link to="/monitors">Monitors</Link> keep a live view of
                    prices, candles, order books or your account data.
                    Strategies can use the values a monitor collects, so
                    monitors are the data source that triggers many automations.
                </p>
            </>
        ),
    },
    {
        id: 'withdrawals',
        title: 'Can the bot withdraw my funds?',
        body: (
            <>
                <p className="mb-0">
                    Not automatically. Withdrawal templates let you prepare a
                    withdrawal, but there is a deliberately enforced delay and a
                    separate confirmation step before any funds move, and only
                    if your API key has withdrawal permission enabled. The
                    simplest safe setup is to never grant that permission to a
                    bot key.
                </p>
            </>
        ),
    },
    {
        id: 'risk',
        title: 'Does the bot guarantee profits?',
        body: (
            <>
                <p className="mb-0">
                    No. Automated trading carries real risk and can lose money.
                    ArgosTraderBot is a tool, not financial advice. Always test
                    with backtests, start with small amounts, and never trade
                    money you cannot afford to lose.
                </p>
            </>
        ),
    },
    {
        id: 'get-started',
        title: 'I am new. Where do I start?',
        body: (
            <>
                <p className="mb-0">
                    Open the <Link to="/onboarding">Getting Started</Link>{' '}
                    checklist. It walks you through connecting your keys,
                    setting up alerts and creating your first automation, and it
                    tracks which steps you have already completed.
                </p>
            </>
        ),
    },
];

function Faq() {
    useEffect(() => {
        trackEvent('faq_view');
        clarityEvent('faq_view');
    }, []);

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">FAQ</h1>
                        <p className="text-gray-600 mb-0">
                            Common questions about setup, safety and how
                            ArgosTraderBot works.
                        </p>
                    </div>
                </div>

                <div className="card card-body border-0 shadow mb-4">
                    <div className="accordion" id="accordionFaq">
                        {FAQ_ITEMS.map(item => (
                            <div className="accordion-item" key={item.id}>
                                <h2
                                    className="accordion-header"
                                    id={'heading-' + item.id}
                                >
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target={'#collapse-' + item.id}
                                        aria-expanded="false"
                                        aria-controls={'collapse-' + item.id}
                                    >
                                        {item.title}
                                    </button>
                                </h2>
                                <div
                                    id={'collapse-' + item.id}
                                    className="accordion-collapse collapse"
                                    aria-labelledby={'heading-' + item.id}
                                    data-bs-parent="#accordionFaq"
                                >
                                    <div className="accordion-body">
                                        {item.body}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Footer />
            </main>
        </>
    );
}

export default Faq;
